<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getIntlLocale } from 'shared/helpers/dateLocale';
import AssistantAPI from 'dashboard/api/assistant';
import AssistantPageLayout from '../components/AssistantPageLayout.vue';
import AssistantSection from '../components/AssistantSection.vue';
import AssistantLoadError from '../components/AssistantLoadError.vue';
import { usePersistentRef } from '../composables/useAssistantCache';

const { t } = useI18n();

const errorText = ref('');
const data = usePersistentRef('analytics');
// Skeleton only on the first visit; later visits show cached numbers
// instantly while a background re-fetch keeps them fresh.
const isFetching = ref(!data.value);
const isRefreshing = ref(false);

const hasData = computed(() => (data.value?.total ?? 0) > 0);

const stats = computed(() => [
  {
    key: 'answered',
    label: t('ASSISTANT.ANALYTICS.ANSWERED_ALONE'),
    value: `${data.value.answered_alone}`,
    note: '',
  },
  {
    key: 'handed',
    label: t('ASSISTANT.ANALYTICS.HANDED_OFF'),
    value: `${data.value.handed_off}`,
    note: '',
  },
  {
    key: 'hours',
    label: t('ASSISTANT.ANALYTICS.HOURS_SAVED'),
    value: `~${data.value.hours_saved}`,
    note: t('ASSISTANT.ANALYTICS.ESTIMATE_NOTE'),
  },
]);

const usageText = computed(() => {
  const usage = data.value?.usage;
  if (!usage || !usage.monthly_limit) return '';
  return t('ASSISTANT.ANALYTICS.USAGE', {
    used: usage.used,
    limit: usage.monthly_limit,
  });
});

const maxWeekly = computed(() =>
  Math.max(1, ...(data.value?.weekly ?? []).map(w => w.replies))
);

// Keep quiet weeks visible next to busy ones; empty weeks stay empty.
const barHeight = replies =>
  replies ? Math.max(4, (replies / maxWeekly.value) * 100) : 0;

const weekLabel = weekStart => {
  const date = new Date(`${weekStart}T00:00:00`);
  return date.toLocaleDateString(getIntlLocale(), {
    month: 'short',
    day: 'numeric',
  });
};

const fetchAnalytics = async () => {
  isRefreshing.value = true;
  isFetching.value = !data.value;
  errorText.value = '';
  try {
    const { data: payload } = await AssistantAPI.getAnalytics();
    data.value = payload;
  } catch (e) {
    errorText.value = t('ASSISTANT.ANALYTICS.LOAD_ERROR');
  } finally {
    isFetching.value = false;
    isRefreshing.value = false;
  }
};

onMounted(fetchAnalytics);
</script>

<template>
  <AssistantPageLayout
    :header-title="t('ASSISTANT.ANALYTICS.TITLE')"
    :description="t('ASSISTANT.ANALYTICS.DESCRIPTION')"
    :is-fetching="isFetching"
  >
    <AssistantLoadError
      v-if="errorText"
      class="mb-5"
      :message="errorText"
      :is-loading="isRefreshing"
      @retry="fetchAnalytics"
    />

    <div
      v-if="!hasData && !errorText"
      class="flex flex-col items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-4 py-16 text-center"
    >
      <span class="text-lg font-medium text-n-slate-12">
        {{ t('ASSISTANT.ANALYTICS.EMPTY_TITLE') }}
      </span>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('ASSISTANT.ANALYTICS.EMPTY_MESSAGE') }}
      </p>
    </div>

    <div v-if="hasData" class="flex min-w-0 flex-col gap-5">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div
          v-for="stat in stats"
          :key="stat.key"
          class="flex min-w-0 flex-col gap-2 p-5 border rounded-lg border-n-weak bg-n-surface-1"
        >
          <span
            class="text-3xl font-semibold tabular-nums text-n-slate-12"
            dir="auto"
          >
            {{ stat.value }}
          </span>
          <span class="text-sm text-n-slate-11">{{ stat.label }}</span>
          <span v-if="stat.note" class="text-xs text-n-slate-10">
            {{ stat.note }}
          </span>
        </div>
      </div>

      <p v-if="usageText" class="text-sm text-n-slate-11">{{ usageText }}</p>

      <AssistantSection
        v-if="data.weekly.length"
        :heading="t('ASSISTANT.ANALYTICS.WEEKLY_TITLE')"
        icon="i-lucide-chart-column"
      >
        <div class="flex items-end gap-3 overflow-x-auto p-4 sm:p-5">
          <div
            v-for="week in data.weekly"
            :key="week.week_start"
            class="flex min-w-12 flex-1 flex-col items-center gap-2"
          >
            <span class="text-sm font-medium tabular-nums text-n-slate-12">{{
              week.replies
            }}</span>
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              class="h-36 w-full max-w-20 text-n-teal-9"
            >
              <rect
                x="0"
                :y="100 - barHeight(week.replies)"
                width="100"
                :height="barHeight(week.replies)"
                rx="3"
                fill="currentColor"
              />
            </svg>
            <span class="text-xs text-center text-n-slate-11">
              {{ weekLabel(week.week_start) }}
            </span>
          </div>
        </div>
      </AssistantSection>

      <AssistantSection
        :heading="t('ASSISTANT.ANALYTICS.UNANSWERED_TITLE')"
        :description="t('ASSISTANT.ANALYTICS.UNANSWERED_HINT')"
        icon="i-lucide-message-circle-question"
      >
        <p
          v-if="!data.unanswered.length"
          class="mb-0 p-4 text-sm text-n-slate-11 sm:p-5"
        >
          {{ t('ASSISTANT.ANALYTICS.UNANSWERED_EMPTY') }}
        </p>
        <ul
          v-else
          class="mb-0 flex flex-col divide-y divide-n-weak px-4 sm:px-5"
        >
          <li
            v-for="item in data.unanswered"
            :key="item.question"
            class="flex flex-wrap items-baseline justify-between gap-2 py-4"
          >
            <span
              dir="auto"
              class="min-w-0 break-words text-sm text-n-slate-12"
            >
              {{ item.question }}
            </span>
            <span class="text-xs shrink-0 tabular-nums text-n-slate-11">
              {{ t('ASSISTANT.ANALYTICS.ASKED_TIMES', { count: item.times }) }}
            </span>
          </li>
        </ul>
      </AssistantSection>
    </div>
  </AssistantPageLayout>
</template>
